import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Save, Phone, Info, MapPin, Mail, Globe, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBilingual = () => ({ ar: '', en: '' });

const emptyState = () => ({
  header: { title: emptyBilingual(), subtitle: emptyBilingual() },
  info: {
    address: emptyBilingual(),
    email: '',
    phone: '',
    website_url: '',
    website_display: '',
    hours: emptyBilingual(),
  },
});

const BilingualField = ({ label, value, onChange, testIdBase, textarea = false, rows = 3 }) => {
  const Comp = textarea ? Textarea : Input;
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Arabic (AR)</Label>
          <Comp
            dir="rtl"
            value={value?.ar || ''}
            onChange={(e) => onChange({ ...(value || {}), ar: e.target.value })}
            data-testid={`${testIdBase}-ar`}
            placeholder="النص بالعربية"
            className="text-right"
            {...(textarea ? { rows } : {})}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">English (EN)</Label>
          <Comp
            value={value?.en || ''}
            onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
            data-testid={`${testIdBase}-en`}
            placeholder="Text in English"
            {...(textarea ? { rows } : {})}
          />
        </div>
      </div>
    </div>
  );
};

export const AdminContactView = () => {
  const { t } = useLanguage();
  const [state, setState] = useState(emptyState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/contact-info`);
        const base = emptyState();
        setState({
          header: { ...base.header, ...(r.data?.header || {}) },
          info: { ...base.info, ...(r.data?.info || {}) },
        });
      } catch {
        toast.error(t('تعذّر تحميل بيانات صفحة تواصل معنا', 'Failed to load Contact-Us content'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const updateSection = (section, key, val) =>
    setState((s) => ({ ...s, [section]: { ...s[section], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/contact-info`, state, { withCredentials: true });
      toast.success(t('تم حفظ بيانات صفحة تواصل معنا', 'Contact-Us content saved'));
    } catch (e) {
      toast.error(t('حدث خطأ أثناء الحفظ', 'Failed to save changes'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center text-slate-500 py-12" data-testid="contact-cms-loading">
        {t('جاري التحميل...', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-contact-cms">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
            <Phone size={22} className="text-[#1565c0]" />
            {t('محتوى صفحة "تواصل معنا"', 'Contact-Us Page Content')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t(
              'حرّر المعلومات التي تظهر في صفحة "تواصل معنا" — سيراها الزوار مباشرة.',
              'Edit the info shown to visitors on the Contact-Us page — updates go live instantly.',
            )}
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="contact-cms-save-button"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>

      {/* Header band */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="contact-cms-header-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Info size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الترويسة العلوية', 'Header Band')}</h3>
        </div>
        <BilingualField
          label={t('العنوان الرئيسي', 'Page Title')}
          value={state.header.title}
          onChange={(v) => updateSection('header', 'title', v)}
          testIdBase="contact-header-title"
        />
        <BilingualField
          label={t('العنوان الفرعي', 'Subtitle')}
          value={state.header.subtitle}
          onChange={(v) => updateSection('header', 'subtitle', v)}
          testIdBase="contact-header-subtitle"
        />
      </Card>

      {/* Contact info */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="contact-cms-info-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <MapPin size={18} className="text-[#1565c0]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('بيانات التواصل', 'Contact Details')}</h3>
        </div>

        <BilingualField
          label={t('العنوان', 'Address')}
          value={state.info.address}
          onChange={(v) => updateSection('info', 'address', v)}
          testIdBase="contact-info-address"
          textarea
          rows={2}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Mail size={14} className="text-emerald-600" />
              {t('البريد الإلكتروني', 'Email')}
            </Label>
            <Input
              type="email"
              value={state.info.email || ''}
              onChange={(e) => updateSection('info', 'email', e.target.value)}
              placeholder="support@example.com"
              data-testid="contact-info-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Phone size={14} className="text-amber-600" />
              {t('رقم الهاتف', 'Phone')}
            </Label>
            <Input
              value={state.info.phone || ''}
              onChange={(e) => updateSection('info', 'phone', e.target.value)}
              placeholder="+961 70 000 000"
              dir="ltr"
              data-testid="contact-info-phone"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Globe size={14} className="text-cyan-600" />
              {t('رابط الموقع', 'Website URL')}
            </Label>
            <Input
              value={state.info.website_url || ''}
              onChange={(e) => updateSection('info', 'website_url', e.target.value)}
              placeholder="https://www.tababa.co"
              data-testid="contact-info-website-url"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">
              {t('نص الرابط الظاهر', 'Website Display Text')}
            </Label>
            <Input
              value={state.info.website_display || ''}
              onChange={(e) => updateSection('info', 'website_display', e.target.value)}
              placeholder="www.tababa.co"
              data-testid="contact-info-website-display"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
            <Clock size={14} className="text-purple-600" />
            {t('ساعات العمل', 'Working Hours')}
          </Label>
          <BilingualField
            label=""
            value={state.info.hours}
            onChange={(v) => updateSection('info', 'hours', v)}
            testIdBase="contact-info-hours"
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="contact-cms-save-button-bottom"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default AdminContactView;
